<template>
    <global-modal v-model="isOpened" width="820px">
        <div class="project-picker">
            <h4 class="project-picker-headline">{{ config.texts.headline }}</h4>

            <div class="form-group project-picker-search">
                <input class="form-control" type="search" autocomplete="off"
                       :placeholder="config.texts.search"
                       :aria-label="config.texts.search"
                       v-model="query">
            </div>

            <p v-if="isEmpty" class="project-picker-empty">{{ emptyText }}</p>

            <ul v-else class="project-picker-list">
                <li v-for="item in items" :key="item.id">
                    <label class="project-picker-item" :class="{'is-selected': isSelected(item.id)}">
                        <input type="checkbox" :checked="isSelected(item.id)" @change="toggle(item.id)">
                        <img class="project-picker-thumb" :class="{'is-placeholder': !item.image}"
                             :src="item.image || PLACEHOLDER" width="56" height="56" alt=""
                             @error="usePlaceholder(item)">
                        <span class="project-picker-text">
                            <span class="project-picker-name">{{ item.name }}</span>
                            <span class="project-picker-note">
                                {{ item.dimensions || '—' }} &middot; {{ item.price || '—' }}
                            </span>
                        </span>
                        <span v-if="item.project" class="project-picker-badge">{{ item.project }}</span>
                    </label>
                </li>
            </ul>

            <button v-if="hasMore" class="project-picker-more" type="button" :disabled="isLoading"
                    @click="loadPage(page + 1)">
                {{ config.texts.loadMore }}
            </button>

            <div class="project-picker-footer">
                <span class="project-picker-count">{{ selectedText }}</span>
                <button class="btn btn-primary small" type="button" :disabled="selected.length === 0" @click="submit">
                    {{ config.texts.submit }}
                </button>
            </div>
        </div>
    </global-modal>
</template>

<script setup lang="ts">
import {GlobalModal} from '@webstacz/shop-components';
import {computed, reactive, ref, watch} from 'vue';
import axios from '@/plugins/axios';

type PickerItem = {
    // "configuration:12" / "product:3" — the two sources number their
    // rows independently, so a plain id would collide
    id: string;
    image: string | null;
    name: string;
    dimensions: string | null;
    price: string | null;
    project: string | null;
};

export type ProjectPickerConfig = {
    availableUrl: string;
    attachUrl: string;
    token: string;
    texts: {
        headline: string;
        search: string;
        submit: string;
        loadMore: string;
        empty: string;
        noneFound: string;
        selected: string;
    };
};

const PLACEHOLDER = '/gfx/cart/noimage.svg';
const SEARCH_DEBOUNCE = 300;

const config = reactive<ProjectPickerConfig>({
    availableUrl: '',
    attachUrl: '',
    token: '',
    texts: {headline: '', search: '', submit: '', loadMore: '', empty: '', noneFound: '', selected: ''},
});

const isOpened = ref(false);
const isLoading = ref(false);
const query = ref('');
const items = ref<PickerItem[]>([]);
const selected = ref<string[]>([]);
const page = ref(1);
const hasMore = ref(false);

let searchTimer: ReturnType<typeof setTimeout> | undefined;
let requestId = 0;

const isEmpty = computed(() => !isLoading.value && items.value.length === 0);

/**
 * An empty result means two different things: nothing left to file, or nothing
 * matching what was typed.
 */
const emptyText = computed(() => query.value.trim() === '' ? config.texts.empty : config.texts.noneFound);

const selectedText = computed(() => config.texts.selected.replace(':count', String(selected.value.length)));

const isSelected = (id: string): boolean => selected.value.includes(id);

/**
 * CP generates the configuration images asynchronously and the media row can
 * outlive the file, so a thumbnail URL is never a guarantee — the same reason
 * the account list carries account-thumbnails.js. Dropping the url falls back
 * to the placeholder and stops the handler from firing again.
 *
 * @param {PickerItem} item
 * @returns {void}
 */
const usePlaceholder = (item: PickerItem): void => {
    if (item.image !== null) {
        item.image = null;
    }
};

const toggle = (id: string): void => {
    selected.value = isSelected(id)
        ? selected.value.filter((selectedId) => selectedId !== id)
        : [...selected.value, id];
};

/**
 * Page 1 replaces the list, any later page appends. Responses are matched
 * against the request counter so a slow first page cannot overwrite the
 * results of a search typed after it.
 *
 * @param {number} requested
 * @returns {Promise<void>}
 */
const loadPage = async (requested: number): Promise<void> => {
    const current = ++requestId;
    isLoading.value = true;

    try {
        const {data} = await axios.get(config.availableUrl, {params: {q: query.value.trim(), page: requested}});

        if (current !== requestId) {
            return;
        }

        items.value = requested === 1 ? data.items : [...items.value, ...data.items];
        hasMore.value = data.hasMore;
        page.value = requested;
    } finally {
        if (current === requestId) {
            isLoading.value = false;
        }
    }
};

/**
 * Posts a real form so the existing attach endpoint keeps handling the write,
 * with its ownership check, redirect and status message.
 *
 * @returns {void}
 */
const submit = (): void => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = config.attachUrl;
    form.hidden = true;

    const token = document.createElement('input');
    token.name = '_token';
    token.value = config.token;
    form.appendChild(token);

    for (const id of selected.value) {
        const input = document.createElement('input');
        input.name = 'item_ids[]';
        input.value = id;
        form.appendChild(input);
    }

    document.body.appendChild(form);
    form.submit();
};

watch(query, () => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => loadPage(1), SEARCH_DEBOUNCE);
});

window.openProjectPickerModal = (pickerConfig: ProjectPickerConfig): void => {
    Object.assign(config, pickerConfig);
    query.value = '';
    items.value = [];
    selected.value = [];
    hasMore.value = false;
    isOpened.value = true;
    loadPage(1);
};
</script>

<style scoped lang="scss">
// GlobalModal already pads its body, so anything here is added on top of it
.project-picker-headline {
    font-size: 20px;
    font-weight: 700;
    line-height: 28px;
    // keeps a long headline clear of the modal's close button
    margin: 0 24px 16px 0;
}

.project-picker-search {
    margin-bottom: 12px;
}

.project-picker-list {
    list-style: none;
    margin: 0;
    padding: 0;
    max-height: 46vh;
    overflow-y: auto;
    // without this the page behind the modal takes over once the list hits its
    // end and the customer scrolls the shop instead of the picker
    overscroll-behavior: contain;
}

.project-picker-item {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    margin: 0 0 8px;
    padding: 10px 12px;
    border: 1px solid #ececec;
    cursor: pointer;
    transition: border-color .15s ease, background-color .15s ease;

    &:hover {
        border-color: #cfcfcf;
    }

    &.is-selected {
        border-color: #000000;
        background: #fafafa;
    }
}

.project-picker-thumb {
    flex: 0 0 auto;
    width: 56px;
    height: 56px;
    object-fit: contain;
    background: #fafafa;
    border: 1px solid #f0f0f0;

    &.is-placeholder {
        padding: 12px;
    }
}

.project-picker-text {
    flex: 1;
    min-width: 0;
}

.project-picker-name {
    display: block;
    font-weight: 600;
}

.project-picker-note {
    display: block;
    margin-top: 2px;
    font-size: 12px;
    color: #888888;
}

.project-picker-badge {
    flex: 0 0 auto;
    padding: 2px 8px;
    border-radius: 10px;
    background: #f0f1f2;
    font-size: 11px;
    font-weight: 600;
    color: #888888;
    white-space: nowrap;
}

.project-picker-more {
    display: block;
    margin: 4px auto 0;
    padding: 8px 0;
    border: 0;
    background: none;
    font-size: 13px;
    font-weight: 600;
    text-decoration: underline;
    cursor: pointer;

    &:disabled {
        opacity: .45;
        cursor: default;
    }
}

.project-picker-empty {
    margin: 0;
    padding: 24px 0;
    color: #888888;
}

.project-picker-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #f0f0f0;

    .btn {
        border: 0;

        &:disabled {
            opacity: .45;
            cursor: not-allowed;
        }
    }
}

.project-picker-count {
    font-size: 13px;
    color: #888888;
}
</style>
